import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { User, Mail, Lock, ArrowLeft } from 'lucide-react';
import GrainTexture from '@/components/GrainTexture';
import GeoPattern from '@/components/GeoPattern';
import { cn } from '@/lib/utils';
import heropaars from '@/assets/heropaars.png';
import heropaarsWebp from '@/assets/heropaars.webp';
import { SEO } from '@/components/SEO';

// SpotlightCard Component
const SpotlightCard = ({ children, className = "", ...props }: any) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(0);
  const [gradientSize, setGradientSize] = useState(600);

  React.useEffect(() => {
    const updateGradientSize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setGradientSize(300);
      } else if (width < 1024) {
        setGradientSize(450);
      } else {
        setGradientSize(600);
      }
    };

    updateGradientSize();
    window.addEventListener('resize', updateGradientSize);
    return () => window.removeEventListener('resize', updateGradientSize);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!divRef.current) return;
    const rect = divRef.current.getBoundingClientRect();
    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleFocus = () => { setOpacity(1); };
  const handleBlur = () => { setOpacity(0); };
  const handleMouseEnter = () => { setOpacity(1); };
  const handleMouseLeave = () => { setOpacity(0); };

  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        "relative overflow-hidden rounded-2xl bg-white shadow-xl transition-all duration-300 hover:shadow-2xl border border-transparent hover:border-[#854C73]/20 h-full",
        className
      )}
      {...props}
    >
      <div
        className="pointer-events-none absolute -inset-px transition duration-300 z-0"
        style={{
          opacity,
          background: `radial-gradient(${gradientSize}px circle at ${position.x}px ${position.y}px, rgba(133, 76, 115, 0.15), transparent 40%)`,
        }}
      />
      <div className="relative z-10 h-full flex flex-col">{children}</div>
    </div>
  );
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || '/mijn-strippenkaart';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Vul alle velden in');
      return;
    }

    setLoading(true);
    const { error } = await signIn(email, password);
    setLoading(false);

    if (error) {
      toast.error('Inloggen mislukt: ' + error.message);
    } else {
      toast.success('Welkom terug!');
      navigate(from, { replace: true });
    }
  };

  return (
    <>
      <SEO 
        title="Inloggen | mijn omgeving"
        description="Log in om je strippenkaarten en gegevens te beheren."
        canonical="https://organisatieopstellingen.com/login"
        noindex={true}
      />
      
      {/* Custom Luxury Header */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-6 overflow-hidden bg-[#854C73]">
        <div className="absolute inset-0 z-0">
          <GrainTexture opacity={0.2} />
          <div className="absolute top-0 right-0 w-[600px] h-[600px] border border-[#F9EBEF]/10 rounded-full translate-x-1/2 -translate-y-1/2 opacity-30"></div>
        </div>
        
        <div className="container mx-auto relative z-10 text-center">
          <Button variant="ghost" asChild className="mb-8 hover:bg-white/20 text-white hover:text-white border border-white/30 mx-auto flex w-fit px-6 py-2 h-auto cursor-pointer">
            <Link to="/">
              <ArrowLeft className="mr-2 w-4 h-4" /> Terug naar home
            </Link>
          </Button>
          
          <h1 className="font-serif text-3xl md:text-5xl text-[#F9EBEF] mb-6">
            Mijn <span className="italic font-light font-serif text-[#F9EBEF]">Omgeving</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-[#F9EBEF]/80 font-serif italic max-w-2xl mx-auto">
            Log in om je strippenkaarten en gegevens te beheren.
          </p>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-12 md:py-24 px-6 relative bg-[#FAEBEE] min-h-[60vh]">
        <GrainTexture opacity={0.1} />
        <GeoPattern />
        
        <div className="container mx-auto max-w-md relative z-10">
          <SpotlightCard className="p-10">
            {/* Icon Circle */}
            <div className="flex justify-center mb-8">
              <div className="w-20 h-20 bg-[#FAEBEE] rounded-full flex items-center justify-center">
                <User size={40} className="text-[#854C73]" strokeWidth={1.5} />
              </div>
            </div>

            {/* Header */}
            <div className="text-center mb-8">
              <h2 
                className="font-serif text-3xl md:text-4xl text-[#854C73] mb-4"
                style={{ fontFamily: "'Noto Serif Display', serif", fontWeight: 700 }}
              >
                Welkom terug
              </h2>
              <p className="text-[#6b4912] text-sm leading-relaxed font-light">
                Log in om toegang te krijgen tot je persoonlijke omgeving
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div className="space-y-2">
                <Label 
                  htmlFor="email" 
                  className="text-xs font-bold tracking-wider uppercase text-[#854C73]"
                >
                  E-mailadres
                </Label>
                <div className="relative">
                  <Mail 
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-[#854C73]/40" 
                    size={18} 
                  />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="jouw@email.nl"
                    required
                    className="pl-12 bg-[#FAEBEE]/30 border-[#854C73]/20 focus:border-[#854C73] focus:ring-[#854C73]/20 text-[#6b4912]"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <Label 
                  htmlFor="password" 
                  className="text-xs font-bold tracking-wider uppercase text-[#854C73]"
                >
                  Wachtwoord
                </Label>
                <div className="relative">
                  <Lock 
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-[#854C73]/40" 
                    size={18} 
                  />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="pl-12 bg-[#FAEBEE]/30 border-[#854C73]/20 focus:border-[#854C73] focus:ring-[#854C73]/20 text-[#6b4912]"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#854C73] hover:bg-[#590D0D] text-white shadow-lg hover:shadow-xl transition-all duration-300 py-6 text-base font-medium"
              >
                {loading ? 'Bezig met inloggen...' : 'Inloggen'}
              </Button>
            </form>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-[#854C73]/10">
              <div className="text-center space-y-3">
                <p className="text-sm text-[#6b4912]">
                  Nog geen account?{' '}
                  <Link 
                    to="/signup" 
                    className="text-[#854C73] hover:text-[#590D0D] font-medium underline underline-offset-2 transition-colors"
                  >
                    Maak er hier een aan
                  </Link>
                </p>
              </div>
            </div>
          </SpotlightCard>
        </div>
      </section>
    </>
  );
}
